<template>
  <PageLayoutWrapper
    v-if="!isLoadingFaculties || !hideFacultyIfNone"
    page="departments/_id"
  >
    <RecentPagePdfButton
      v-if="enableDownloadPDFButton"
      class="absolute right-2 top-13"
      :page-id="'department-' + department.id"
    />
    <h1 class="p-4 md:p-8 pb-2 mb-0">Department: {{ department.displayName }}</h1>
    <p
      v-if="department.catalogDescription && enableCatalogDescriptions"
      class="md:px-8 md:pb-8"
      data-test="curriculumDepartmentCatalogDescription"
    >
      {{ department.catalogDescription }}
    </p>
    <ul
      class="flex border-b px-4 md:px-8"
      data-test="dept-tab-list"
    >
      <li
        v-for="tab of tabs"
        :key="tab.to"
        class="-mb-px mr-1"
        data-test="tab"
      >
        <nuxt-link
          class="bg-white inline-block py-2 px-2 md:px-4 text-blue-600 font-semibold ui-font"
          :to="`/departments/${safeEncodeURIComponent($route.params.id)}/${tab.to}`"
        >
          {{ tab.label }}
        </nuxt-link>
      </li>
    </ul>
    <div
      class="px-4 md:px-8 pb-8 pt-2"
      data-test="tab-content"
    >
      <nuxt-child
        keep-alive
        :department="department"
        :page="page"
      />
    </div>
  </PageLayoutWrapper>
</template>

<script>
import { mapGetters } from 'vuex'
import { ref } from '@nuxtjs/composition-api'
import RecentPagePdfButton from '@/components/RecentPagePdfButton.vue'
import PageLayoutWrapper from '@/components/PageLayoutWrapper'
import pageLayout from '@/mixins/pageLayoutMixin'
import { safeEncodeURIComponent } from '@/utils/generalUtils'
import useFeatureFlags from '@/composables/useFeatureFlags'
import useMeta from '@/composables/useMeta'
import {
  DEPARTMENT_CATALOG_DESCRIPTIONS,
  DEPARTMENT_PAGE_PDF,
  HIDE_DEPARTMENTS,
  HIDE_FACULTY_IF_NONE,
} from '@/constants/featureFlags'

const DEFAULT_TABS = [
  { to: 'overview', label: 'Overview' },
  { to: 'courses', label: 'Courses' },
  { to: 'faculty', label: 'Faculty' },
  { to: 'programs', label: 'Programs' },
]

export default {
  components: { PageLayoutWrapper, RecentPagePdfButton },
  mixins: [pageLayout],
  setup() {
    const faculties = ref([])
    const isLoadingFaculties = ref(true)
    const {
      enableCatalogDescriptions,
      enableDownloadPDFButton,
      hideDepartments,
      hideFacultyIfNone,
    } = useFeatureFlags({
      enableCatalogDescriptions: DEPARTMENT_CATALOG_DESCRIPTIONS,
      enableDownloadPDFButton: DEPARTMENT_PAGE_PDF,
      hideDepartments: HIDE_DEPARTMENTS,
      hideFacultyIfNone: HIDE_FACULTY_IF_NONE,
    })
    const { catalogWideMetaTitle } = useMeta()
    return {
      enableCatalogDescriptions,
      enableDownloadPDFButton,
      hideDepartments,
      hideFacultyIfNone,
      faculties,
      isLoadingFaculties,
      safeEncodeURIComponent,
      catalogWideMetaTitle,
    } // now we can use enableCatalogDescriptions for v-if to enable or disable in template
  },
  head() {
    return {
      title: `${this.department.displayName} Department | ${
        this.catalogWideMetaTitle
      }`,
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: `${this.department.displayName} Department | ${
            this.catalogWideMetaTitle
          }`,
        },
        ...(this.hideDepartments
          ? [{ hid: 'robots', name: 'robots', content: 'noindex' }]
          : []),
      ],
    }
  },
  computed: {
    ...mapGetters('settings', [
      'instructorsFilters',
      'school',
    ]),
    tabs() {
      return this.hideFacultyIfNone
        ? this.faculties.length
          ? DEFAULT_TABS
          : DEFAULT_TABS.filter((tab) => tab.to !== 'faculty')
        : DEFAULT_TABS
    },
  },
  async asyncData({ params, $axios, $bugsnag, redirect, store, route }) {
    const activeTab = route.fullPath.split('/').pop().split('?')[0]
    // when navigating from search there's no tab provided
    // so activeTab may have a value not matching any of the tabs
    // if so, we set it to first available tab
    if (!DEFAULT_TABS.map((tab) => tab.to).includes(activeTab)) {
      redirect(`${route.fullPath}/${DEFAULT_TABS[0].to}`)
    }
    const url = `/ca/${store.state.settings.school}/catalogPages/${
      store.state.settings.activeCatalog
    }/department-${safeEncodeURIComponent(params.id)}`
    try {
      const [department, page] = await Promise.all([
        $axios.$get(
          `/${store.state.settings.school}/departments/${safeEncodeURIComponent(
            params.id
          )}`
        ),
        $axios.$get(url).catch((e) => {
          console.warn('Missing department page')
        }),
      ])

      return {
        department,
        page,
      }
    } catch (e) {
      $bugsnag.notify(e)
      redirect('/404')
    }
  },
  async created() {
    this.$store.commit(
      'page/SET_CURRENT_PAGE_LABEL',
      this.department.displayName
    )
    if (this.hideFacultyIfNone) {
      this.faculties = await this.fetchFaculties()
      this.isLoadingFaculties = false
    }
  },
  methods: {
    async fetchFaculties() {
      const returnFields = ['_id']
      const hasFilters =
        this.instructorsFilters?.filters &&
        this.instructorsFilters?.filters.length
      const professors = hasFilters
        ? await this.$axios.$post(
            `/${this.school}/professors/search`,
            this.instructorsFilters,
            {
              params: {
                catalogDepartments: safeEncodeURIComponent(this.$route.params.id),
                returnFields,
                searchQuery: '$filters',
              },
            }
          )
        : await this.$axios.$get(`/${this.school}/professors`, {
            params: {
              catalogDepartments: safeEncodeURIComponent(this.$route.params.id),
              returnFields,
            },
          })

      return Object.values(professors || {})
    },
  },
}
</script>

<style lang="css" scoped>
.nuxt-link-active {
  @apply border-l border-t border-r rounded-t;
}
.ui-font,
.ui-font:hover,
.ui-font:focus {
  font-weight: var(--interfaceFontWeight, inherit);
  color: var(--interfaceFontColor, inherit);
}
</style>
